import { useEffect, useState } from "react"
import { api } from "../../lib/api"
import type { Desempenho as DesempenhoTipo, PontoDeEvolucao, UnidadeAcumulada } from "../../types"
import { dataCurta, nota as formatarNota } from "../../lib/format"
import { Card, Tile } from "../ui"
import { listar } from "../../lib/texto"

// A prova da Marinha vale dez pontos e aprova com cinco. Toda leitura de nota
// aqui usa essa régua — é a que o candidato vai encontrar na Capitania, e
// inventar uma escala nossa só criaria uma segunda régua para ele traduzir.
const LINHA_DE_CORTE = 5

/**
 * Boletim acumulado do candidato NUMA habilitação. Duas metades que se
 * completam: em cima, o que já foi conquistado (motiva); embaixo, onde ele
 * perde ponto (direciona).
 *
 * Por habilitação, e não geral, porque nada aqui é comparável entre as duas:
 * Motonauta são 20 questões com corte 10, Arrais são 40 com corte 20, e o
 * programa do Motonauta é mais estreito — "foque em Comunicações Náuticas"
 * para quem só vai fazer Motonauta é mandar estudar o que não cai. O painel
 * renderiza um por habilitação; para quem tem uma só, que é quase todo mundo,
 * a tela é a mesma.
 *
 * Não renderiza nada antes do primeiro simulado no formato da prova — o
 * diagnóstico não entra em `total` (ver Simulados::Desempenho), e um boletim
 * feito só de seis questões sem cronômetro seria ruído com cara de dado.
 */
export default function Desempenho({ slug, nome }: { slug: string; nome: string }) {
  const [dados, setDados] = useState<DesempenhoTipo | null | undefined>(undefined)

  useEffect(() => {
    api
      .get<DesempenhoTipo>(`/me/desempenho?habilitacao=${encodeURIComponent(slug)}`)
      .then(setDados)
      .catch(() => setDados(null))
  }, [slug])

  if (!dados || dados.total === 0) return null

  const respondidas = dados.por_unidade.filter((u) => u.total > 0)
  if (respondidas.length === 0) return null

  const acertos = respondidas.reduce((soma, u) => soma + u.acertos, 0)
  const questoes = respondidas.reduce((soma, u) => soma + u.total, 0)
  const geral = Math.round((100 * acertos) / questoes)

  // Evolução = última menos a primeira da série. Com um ponto só não há
  // trajetória, e fingir que há seria mentir sobre o próprio progresso.
  const curva = dados.evolucao
  const delta = curva.length >= 2 ? curva[curva.length - 1].nota - curva[0].nota : null

  const fracas = [...respondidas].sort((a, b) => a.aproveitamento - b.aproveitamento).filter((u) => u.aproveitamento < 50)

  return (
    <div className="space-y-6">
      <section aria-label="Seu progresso" className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-600">Seu progresso em {nome}</h2>

        <Card>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4">
            <Tile rotulo={dados.total === 1 ? "simulado feito" : "simulados feitos"} valor={String(dados.total)} />
            <Tile rotulo="melhor nota" valor={formatarNota(dados.melhor)} tom="ouro" />
            <Tile rotulo="média geral" valor={formatarNota(dados.media)} />
            <Tile
              rotulo="evolução"
              valor={delta === null ? "—" : `${delta >= 0 ? "+" : "−"}${formatarNota(Math.abs(delta))}`}
              nota={delta === null ? "faça mais um" : "desde o primeiro"}
              tom={delta === null ? "neutro" : delta > 0.05 ? "bom" : delta < -0.05 ? "ruim" : "neutro"}
            />
          </dl>
        </Card>

        {curva.length >= 2 && (
          <Card>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-600">Evolução da nota</p>
            <p className="mt-1 text-xs text-slate-500">
              Seus últimos {curva.length} simulados no formato da prova · a linha tracejada é a nota de corte
            </p>
            <Curva pontos={curva} />
          </Card>
        )}
      </section>

      <section aria-label="Onde focar" className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-600">Onde focar em {nome}</h2>

        <Card destaque>
          <p className="text-sm text-navy-900">
            {fracas.length > 0 ? (
              <>
                Você acerta <strong className="font-semibold">{geral}%</strong> no geral. O ganho mais rápido está em{" "}
                <strong className="font-semibold">{listar(fracas.slice(0, 3).map((u) => u.unidade))}</strong> — abaixo
                de metade das questões.
              </>
            ) : (
              <>
                <strong className="font-semibold">{geral}% de acerto</strong> no acumulado, sem nenhuma unidade abaixo
                da metade. Siga treinando para firmar o ritmo antes da prova.
              </>
            )}
          </p>
        </Card>

        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-600">
            Aproveitamento por unidade
          </p>
          <p className="mt-1 text-xs text-slate-500">Somando todas as {questoes} questões que você já respondeu</p>
          <BarrasPorUnidade unidades={respondidas} />
        </Card>
      </section>
    </div>
  )
}

/**
 * Aproveitamento por unidade, do MAIOR progresso para o menor. A primeira coisa
 * que a pessoa vê é o quanto já domina, e a lista desce até o que falta — quem
 * abre o painel na semana da prova precisa sair dali com combustível, não com
 * uma lista de buracos na cara.
 *
 * Cor única, e verde, mesmo nas unidades fracas. Pintar de vermelho o que está
 * baixo transforma o boletim numa lista de fracassos, e é a mesma armadilha que
 * a `Barra` do simulado já evita: "3 de 5 dominadas" e "você erra 40%" são o
 * mesmo número, mas um é conquista acumulada e o outro é dívida. O
 * direcionamento vem do selo "focar" e da frase acima, que apontam sem acusar.
 */
function BarrasPorUnidade({ unidades }: { unidades: UnidadeAcumulada[] }) {
  const ordenadas = [...unidades].sort((a, b) => b.aproveitamento - a.aproveitamento)
  // A pior é o fim da lista agora, mas calculo por valor e não por posição: se a
  // ordenação mudar de novo, o selo não sai marcando a unidade errada.
  const pior =
    unidades.length > 1 ? unidades.reduce((a, b) => (b.aproveitamento < a.aproveitamento ? b : a)) : null

  return (
    <ul className="mt-4 space-y-3.5">
      {ordenadas.map((u) => {
        const foco = pior?.codigo === u.codigo
        return (
          <li key={u.codigo}>
            <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-navy-800">
                <span className="font-mono text-xs text-slate-500">{u.codigo}</span>
                {u.unidade}
                {foco && (
                  <span className="rounded-full bg-red-100 px-1.5 py-0.5 font-mono text-[0.6rem] font-semibold uppercase tracking-wider text-red-700">
                    focar
                  </span>
                )}
              </span>
              <span className="shrink-0 font-mono text-xs tabular-nums text-slate-500">
                {u.acertos}/{u.total} · <span className="font-semibold text-navy-900">{u.aproveitamento}%</span>
              </span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-navy-50"
              role="progressbar"
              aria-valuenow={u.aproveitamento}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${u.unidade}: ${u.aproveitamento}% de acerto`}
            >
              <div
                className="h-full rounded-full bg-emerald-500 transition-all duration-700"
                style={{ width: `${u.aproveitamento}%` }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/** Curva da nota ao longo dos simulados, com a linha de corte como referência. */
function Curva({ pontos }: { pontos: PontoDeEvolucao[] }) {
  // viewBox largo e sem altura fixa: com um retrato quadrado o `meet` encolhia
  // o gráfico para caber na altura e sobrava faixa branca dos dois lados.
  const L = 640
  const A = 150
  const margemX = 20
  const margemTopo = 26
  const margemBase = 18
  const n = pontos.length

  const x = (i: number) => (n === 1 ? L / 2 : margemX + (i * (L - 2 * margemX)) / (n - 1))
  const y = (valor: number) => margemTopo + (1 - Math.min(10, Math.max(0, valor)) / 10) * (A - margemTopo - margemBase)

  const linha = pontos.map((p, i) => `${x(i)},${y(p.nota)}`).join(" ")
  const area = `${x(0)},${A - margemBase} ${linha} ${x(n - 1)},${A - margemBase}`
  const corte = y(LINHA_DE_CORTE)

  return (
    <svg
      viewBox={`0 0 ${L} ${A}`}
      className="mt-3 w-full"
      role="img"
      aria-label="Evolução da sua nota nos últimos simulados"
    >
      <defs>
        <linearGradient id="curva-nota" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-gold-500)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--color-gold-500)" stopOpacity="0" />
        </linearGradient>
      </defs>

      <line x1={margemX} y1={corte} x2={L - margemX} y2={corte} stroke="var(--color-navy-200)" strokeDasharray="4 5" />
      <text x={L - margemX} y={corte - 6} textAnchor="end" className="fill-navy-400 text-[11px]">
        5,0 — corte
      </text>

      <polygon points={area} fill="url(#curva-nota)" />
      <polyline
        points={linha}
        fill="none"
        stroke="var(--color-gold-500)"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {pontos.map((p, i) => {
        const ultimo = i === n - 1
        return (
          <g key={p.id}>
            <circle
              cx={x(i)}
              cy={y(p.nota)}
              r={ultimo ? 5 : 3.5}
              fill={ultimo ? "var(--color-gold-600)" : "var(--color-gold-500)"}
              stroke="white"
              strokeWidth={ultimo ? 2 : 1.5}
            >
              <title>{`${dataCurta(p.finalizada_em)}: ${formatarNota(p.nota)}`}</title>
            </circle>
            {ultimo && (
              <text x={x(i)} y={y(p.nota) - 14} textAnchor="middle" className="fill-navy-900 text-[13px] font-bold">
                {formatarNota(p.nota)}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}
